import { defineStore, storeToRefs } from 'pinia'
import { collection, query, where } from '@firebase/firestore'
import { useOrganizationsStore } from '~/store/organizations'
import type { Workspace } from '~/types/workspaces'
import { Collections } from '~/constants/firebase'
import { WorkspaceRoles, WorkspaceVisibility } from '~/constants/workspaces'

type UserWorkspace = { workspace: Workspace, role: WorkspaceRoles }

export const useWorkspacesStore = defineStore('workspacesStore', () => {
  const organizationsStore = useOrganizationsStore()
  const { selectedOrganization } = storeToRefs(organizationsStore)

  const userUid = ref<string | null>(null)
  const useOrganizationsCollectionRef = computed(() =>
    userUid.value && selectedOrganization.value
      ? collection(
          useGetFirestore(),
          Collections.Users.Organizations.workspaces(userUid.value, selectedOrganization.value.id),
        )
      : null,
  )
  const { data: userWorkspaces, promise: userWorkspacesPromise } = useCollection<UserWorkspace>(
    useOrganizationsCollectionRef,
    {
      wait: true,
      // Due to issue => https://github.com/vuejs/vuefire/issues/1315
      ssrKey: userUid.value && selectedOrganization.value
        ? Collections.Users.Organizations.workspaces(userUid.value, selectedOrganization.value.id)
        : undefined,
    },
  )

  const publicWorkspacesPath = computed(() => selectedOrganization.value
    ? Collections.Workspaces.base(selectedOrganization.value.id)
    : null,
  )
  const publicWorkspacesCollection = computed(() => publicWorkspacesPath.value
    ? query(
        collection(useGetFirestore(), publicWorkspacesPath.value),
        where('visibility', '==', WorkspaceVisibility.Public),
      )
    : null,
  )
  const { data: publicWorkspaces, promise: publicWorkspacesPromise } = useCollection<Workspace>(
    publicWorkspacesCollection,
    {
      wait: true,
      // Due to issue => https://github.com/vuejs/vuefire/issues/1315
      ssrKey: publicWorkspacesPath.value ?? undefined,
    },
  )

  const workspaces = computed((): UserWorkspace[] => [
    ...userWorkspaces.value,
    ...publicWorkspaces.value
      .filter(
        publicWorkspace => !userWorkspaces.value
          .map(workspace => workspace.workspace.id)
          .includes(publicWorkspace.id),
      )
      .map(publicWorkspace => ({
        role: WorkspaceRoles.User,
        workspace: publicWorkspace,
      })),
  ])

  async function getWorkspaces() {
    if (!selectedOrganization.value)
      throw new Error('Must have to select an organization before fetching related workspaces!')

    const user = await getCurrentUser()
    userUid.value = user.uid
    await nextTick() // Explain in this issue : https://github.com/vuejs/vuefire/discussions/1451
    await Promise.all([publicWorkspacesPromise.value, userWorkspacesPromise.value])
  }

  const selectedWorkspace = ref<UserWorkspace | null>(null)
  const selectedWorkspaceRole = ref<WorkspaceRoles | null>(null)
  function setSelectedWorkspaceRole() {
    selectedWorkspaceRole.value = workspaces.value.find(
      // `workspace && ` added due to issue with Courtinet
      ({ workspace }) => workspace && workspace.id === selectedWorkspace.value?.workspace.id,
    )?.role ?? null
  }
  function setSelectedWorkspace(value: UserWorkspace | null) {
    selectedWorkspace.value = value
    setSelectedWorkspaceRole()

    const { $sentrySetTag } = useNuxtApp()
    $sentrySetTag('workspaceId', value?.workspace.id)
  }

  function $reset() {
    userWorkspaces.value = []
    publicWorkspaces.value = []
    selectedWorkspace.value = null
    selectedWorkspaceRole.value = null
  }

  return {
    workspaces,
    getWorkspaces,
    selectedWorkspace,
    selectedWorkspaceRole,
    setSelectedWorkspace,
    $reset,
  }
})
